import React, { useEffect, useState } from 'react';
import Modal from './Modal';

/**
 * Guided ebook-creation wizard (#235, #254). Full flow:
 *   Brand → Topic → Style (+ upload) → Voice → Preview (4 variants) →
 *   Create & plan → Review chapters → Generate.
 *
 * The wizard owns the whole lifecycle (create-volume → plan-pause → chapter
 * edit → pipeline) via the dashboard endpoints; the parent just opens it and is
 * told the created slug via onDone so it can refresh + open the volume.
 */

interface Preset { id: string; label: string; description: string }
interface Variant { id: string; baseId: string; label: string; description: string; override: unknown; cssVars: string; variantClass?: string }
interface PlannedChapter { slug?: string; title: string; purpose: string; wordTarget: number; _key?: string }
let _chapKeySeq = 0;
const chapKey = (): string => `ch-${_chapKeySeq++}`; // stable React key across reorder (not sent to server)

interface EbookWizardProps {
  brand: string;
  onClose: () => void;
  /** Called with the created volume slug (+ brand) once generation is triggered. */
  onDone: (slug: string, brand: string) => void;
}

type Step = 'brand' | 'topic' | 'style' | 'voice' | 'preview' | 'review';
const STEPS: Step[] = ['brand', 'topic', 'style', 'voice', 'preview', 'review'];
const STEP_LABEL: Record<Step, string> = {
  brand: 'Brand', topic: 'Topic', style: 'Style', voice: 'Voice', preview: 'Preview', review: 'Chapters',
};

// The ZopDev ebook brand family. Each has brands/{brand}/ebook-tokens.yaml.
const EBOOK_BRANDS = ['zopdev', 'zopnight', 'zopday'];

function PresetCards({ items, selected, onSelect }: { items: Preset[]; selected: string; onSelect: (id: string) => void }) {
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 8, maxHeight: 280, overflowY: 'auto' }}>
      {items.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onSelect(p.id)}
          style={{
            textAlign: 'left', padding: '10px 12px', borderRadius: 0, cursor: 'pointer',
            border: `1px solid ${selected === p.id ? 'var(--accent, #0891b2)' : 'var(--border)'}`,
            background: selected === p.id ? 'var(--accent-light, rgba(8,145,178,0.08))' : 'transparent',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{p.label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{p.description}</div>
        </button>
      ))}
    </div>
  );
}

// cssVars now arrives as flat declarations; scope them under a per-variant class.
const variantClass = (id: string): string => `vp-${id.replace(/[^a-z0-9]/gi, '')}`;

const PG_EYEBROW: React.CSSProperties = { fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '.1em', color: 'var(--zop-orange)', textTransform: 'uppercase' };

/** The four layout keys (#294). Derived from `variantClass` (`ebook--bold` →
 *  `bold`), falling back to the id suffix (`default--bold` → `bold`). */
type VariantKey = 'signature' | 'bold' | 'warm' | 'contrast';
function variantKey(v: Variant): VariantKey {
  const raw = (v.variantClass?.replace(/^ebook--/, '') || v.id.split('--').pop() || '').toLowerCase();
  return (['signature', 'bold', 'warm', 'contrast'] as VariantKey[]).includes(raw as VariantKey) ? (raw as VariantKey) : 'signature';
}
const SERIF = "Georgia,'Iowan Old Style','Times New Roman',serif";

/** A compact, clickable preview card that renders a MINI COVER + DIVIDER + STAT
 *  + PROSE line in the variant's real look (#294) — layout, type stack, cover
 *  alignment, divider and stat treatment all differ per variant, not just color. */
function VariantPreview({ v, selected, onSelect }: { v: Variant; selected: boolean; onSelect: () => void }) {
  const cls = variantClass(v.id);
  const k = variantKey(v);
  const headFont = k === 'warm' ? SERIF : 'var(--font)';
  const bodyFont = k === 'warm' ? SERIF : 'var(--body)';
  const centered = k === 'signature';

  // Mini cover: dark for contrast, cream otherwise; centered for signature.
  const coverDark = k === 'contrast';
  const cover: React.CSSProperties = {
    background: coverDark ? 'var(--ink)' : 'var(--paper)', color: coverDark ? 'var(--paper)' : 'var(--ink)',
    padding: 12, textAlign: centered ? 'center' : 'left',
    borderBottom: coverDark ? '4px solid var(--zop-orange)' : 'none',
  };
  // Mini chapter divider: inked band; bold gets a slab rule, contrast an accent bar.
  const divider: React.CSSProperties = {
    background: 'var(--ink)', color: 'var(--paper)', padding: '10px 12px', display: 'flex', alignItems: 'baseline', gap: 8,
    borderLeft: k === 'contrast' ? '8px solid var(--zop-orange)' : 'none',
  };

  return (
    <button type="button" onClick={onSelect}
      style={{ textAlign: 'left', padding: 0, borderRadius: 0, cursor: 'pointer', overflow: 'hidden',
        border: `2px solid ${selected ? 'var(--accent, #0891b2)' : 'var(--border)'}` }}>
      <style>{`.${cls}{${v.cssVars}}`}</style>
      <div className={cls}>
        {/* COVER */}
        <div style={cover}>
          <div style={PG_EYEBROW}>Cover</div>
          <div style={{ fontFamily: headFont, fontWeight: k === 'warm' ? 500 : 800, fontStyle: k === 'warm' ? 'italic' : 'normal', fontSize: k === 'bold' ? 22 : 18, lineHeight: 1.02, marginTop: 3, textTransform: k === 'bold' ? 'uppercase' : 'none' }}>The FinOps Handbook</div>
          <div style={{ fontFamily: bodyFont, fontSize: 10, opacity: 0.7, marginTop: 3 }}>ZopDev · 2026</div>
        </div>
        {/* DIVIDER */}
        <div style={divider}>
          <div style={{ fontFamily: headFont, fontWeight: k === 'bold' ? 800 : 400, fontStyle: k === 'warm' ? 'italic' : 'normal', fontSize: k === 'bold' ? 30 : 22, color: 'var(--zop-orange)', lineHeight: 0.8 }}>01</div>
          <div style={{ fontFamily: headFont, fontWeight: 600, fontSize: 11, borderTop: k === 'bold' ? '4px solid var(--zop-orange)' : 'none', paddingTop: k === 'bold' ? 3 : 0, textTransform: k === 'bold' ? 'uppercase' : 'none' }}>Why bills drift</div>
        </div>
        {/* STAT + PROSE */}
        <div style={{ background: k === 'contrast' ? 'var(--ink)' : 'var(--paper)', color: k === 'contrast' ? 'var(--paper)' : 'var(--ink)', padding: 12, borderLeft: k === 'contrast' ? '4px solid var(--zop-orange)' : 'none' }}>
          <div style={{ fontFamily: headFont, fontWeight: k === 'bold' ? 800 : 700, fontSize: k === 'bold' ? 44 : 30, color: 'var(--zop-orange)', lineHeight: 1, letterSpacing: k === 'bold' ? '-.03em' : '0' }}>168</div>
          <div style={{ fontFamily: bodyFont, fontSize: 10, color: 'var(--g-500)', marginTop: 2 }}>cloud spend recovered</div>
          {k === 'warm'
            ? <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, textAlign: 'center', margin: '8px 0 2px', padding: '6px 0', borderTop: '1px solid var(--zop-orange)', borderBottom: '1px solid var(--zop-orange)', lineHeight: 1.3 }}>“Idle is the default.”</div>
            : <div style={{ height: 6, marginTop: 8, background: 'var(--zop-blue)', width: '70%' }} />}
        </div>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{v.label}{selected ? ' ✓' : ''}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{v.description}</div>
      </div>
    </button>
  );
}

/** Four sample pages — one of each page type — in the chosen variant's real
 *  style (#294), so the user sees the look across cover / chapter / data / prose:
 *  type stack (serif for warm), cover alignment, divider slab, stat scale and
 *  pull-quote treatment all track the variant. */
function VariantPages({ v }: { v: Variant }) {
  const cls = `${variantClass(v.id)}-pg`;
  const k = variantKey(v);
  const headFont = k === 'warm' ? SERIF : 'var(--font)';
  const bodyFont = k === 'warm' ? SERIF : 'var(--body)';
  const centered = k === 'signature';
  const page: React.CSSProperties = { flex: '0 0 150px', height: 200, borderRadius: 0, border: '1px solid var(--line)', padding: 12, overflow: 'hidden', background: 'var(--paper)', color: 'var(--ink)', display: 'flex', flexDirection: 'column' };
  const coverDark = k === 'contrast';
  return (
    <div style={{ marginTop: 12 }}>
      <style>{`.${cls}{${v.cssVars}}`}</style>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>Sample pages in <strong>{v.label}</strong> — scroll to see all four →</div>
      <div className={cls} style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
        {/* Cover */}
        <div style={{ ...page, textAlign: centered ? 'center' : 'left', background: coverDark ? 'var(--ink)' : 'var(--paper)', color: coverDark ? 'var(--paper)' : 'var(--ink)', borderBottom: coverDark ? '4px solid var(--zop-orange)' : '1px solid var(--line)' }}>
          <div style={PG_EYEBROW}>Cover</div>
          <div style={{ fontFamily: headFont, fontWeight: k === 'warm' ? 500 : 800, fontStyle: k === 'warm' ? 'italic' : 'normal', fontSize: k === 'bold' ? 30 : 26, lineHeight: 1.02, marginTop: 22, textTransform: k === 'bold' ? 'uppercase' : 'none' }}>The FinOps Handbook</div>
          <div style={{ fontFamily: bodyFont, fontSize: 11, opacity: 0.7, marginTop: 'auto' }}>ZopDev · 2026</div>
        </div>
        {/* Chapter divider */}
        <div style={{ ...page, background: 'var(--ink)', color: 'var(--paper)', borderLeft: k === 'contrast' ? '10px solid var(--zop-orange)' : '1px solid var(--line)' }}>
          <div style={PG_EYEBROW}>Chapter</div>
          <div style={{ fontFamily: headFont, fontWeight: k === 'bold' ? 800 : 400, fontStyle: k === 'warm' ? 'italic' : 'normal', fontSize: 64, lineHeight: 1, color: 'var(--zop-orange)', marginTop: 'auto' }}>01</div>
          <div style={{ fontFamily: headFont, fontWeight: 700, fontSize: 16, marginTop: 6, borderTop: k === 'bold' ? '5px solid var(--zop-orange)' : 'none', paddingTop: k === 'bold' ? 5 : 0, textTransform: k === 'bold' ? 'uppercase' : 'none' }}>Why cloud bills drift</div>
        </div>
        {/* Data */}
        <div style={{ ...page, background: k === 'contrast' ? 'var(--ink)' : 'var(--paper)', color: k === 'contrast' ? 'var(--paper)' : 'var(--ink)', borderLeft: k === 'contrast' ? '4px solid var(--zop-orange)' : '1px solid var(--line)' }}>
          <div style={PG_EYEBROW}>Data</div>
          <div style={{ fontFamily: headFont, fontWeight: 800, fontSize: k === 'bold' ? 60 : 42, color: 'var(--zop-orange)', lineHeight: 1, marginTop: 8, letterSpacing: k === 'bold' ? '-.03em' : '0' }}>28%</div>
          <div style={{ fontFamily: bodyFont, fontSize: 10, color: 'var(--g-500)' }}>recovered in 30 days</div>
          {[82, 56, 34].map((w, i) => <div key={i} style={{ height: k === 'bold' ? 9 : 7, marginTop: 7, background: 'var(--zop-blue)', width: `${w}%`, borderRadius: 0 }} />)}
        </div>
        {/* Prose */}
        <div style={{ ...page, columnCount: k === 'warm' ? 2 : 1, columnGap: 10 } as React.CSSProperties}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '.1em', color: 'var(--zop-orange)', textTransform: 'uppercase' }}>Prose</div>
          <div style={{ fontFamily: bodyFont, fontSize: 10, lineHeight: 1.5, marginTop: 8, color: 'var(--g-700)' }}>Dev environments run around the clock but get used a third of the day. The waste compounds quietly, and no one owns the drift.</div>
          <div style={{ fontFamily: headFont, fontWeight: k === 'warm' ? 500 : 700, fontStyle: k === 'warm' ? 'italic' : 'normal', fontSize: 16, color: k === 'contrast' ? 'var(--zop-orange)' : 'var(--zop-blue)', marginTop: 8, lineHeight: 1.1, textAlign: k === 'warm' ? 'center' : 'left', borderTop: k === 'warm' ? '1px solid var(--zop-orange)' : 'none', borderBottom: k === 'warm' ? '1px solid var(--zop-orange)' : 'none', padding: k === 'warm' ? '5px 0' : '0' }}>“Idle is the default.”</div>
        </div>
      </div>
    </div>
  );
}

export default function EbookWizard({ brand: initialBrand, onClose, onDone }: EbookWizardProps) {
  const [step, setStep] = useState<Step>('brand');
  const [brand, setBrand] = useState(EBOOK_BRANDS.includes(initialBrand) ? initialBrand : 'zopdev');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [chapters, setChapters] = useState(5);

  const [themes, setThemes] = useState<Preset[]>([]);
  const [styles, setStyles] = useState<Preset[]>([]);
  const [designs, setDesigns] = useState<Preset[]>([]);
  const [baseTheme, setBaseTheme] = useState('default');
  const [contentStyle, setContentStyle] = useState('default');
  const [designTemplate, setDesignTemplate] = useState(''); // '' = engine default renderer

  const [variants, setVariants] = useState<Variant[]>([]);
  const [chosenVariant, setChosenVariant] = useState<Variant | null>(null);
  const [designTheme, setDesignTheme] = useState('default'); // resolved id sent to create

  const [planned, setPlanned] = useState<PlannedChapter[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    // Reset style-derived state on brand change: a custom theme id from the
    // previous brand wouldn't exist for the new one (server would fall back to
    // an empty override → four look-alike variants with no error).
    setBaseTheme('default'); setContentStyle('default'); setVariants([]); setChosenVariant(null); setDesignTemplate('');
    fetch(`/api/${brand}/ebook-presets`).then((r) => r.json()).then((d) => { setThemes(d.themes ?? []); setStyles(d.styles ?? []); setDesigns(d.designs ?? []); }).catch(() => {});
  }, [brand]);

  const stepIdx = STEPS.indexOf(step);
  const canTopic = title.trim().length > 0;

  async function uploadStyle(file: File) {
    setBusy(true); setMsg('Importing style…');
    try {
      const html = await file.text();
      const r = await fetch(`/api/${brand}/ebook-styles/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html, label: file.name.replace(/\.html?$/i, '') }),
      });
      const d = await r.json();
      if (d?.ok && d.theme) {
        const p = await fetch(`/api/${brand}/ebook-presets`).then((x) => x.json());
        setThemes(p.themes ?? []);
        setBaseTheme(d.theme.id);
        setMsg(`Imported "${d.theme.label}"${d.warnings?.length ? ` (${d.warnings.length} warning)` : ''}`);
      } else setMsg(`Error: ${d?.error ?? 'import failed'}`);
    } catch (e) { setMsg(`Error: ${(e as Error).message}`); }
    finally { setBusy(false); }
  }

  async function loadVariants() {
    setBusy(true); setMsg('Generating style variants…'); setVariants([]); setChosenVariant(null);
    try {
      const r = await fetch(`/api/${brand}/ebook-style-variants`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ baseThemeId: baseTheme, voice: contentStyle }),
      });
      const d = await r.json();
      if (d?.ok) setVariants(d.variants ?? []);
      else setMsg(`Error: ${d?.error ?? 'could not build variants'}`);
      setMsg('');
    } catch (e) { setMsg(`Error: ${(e as Error).message}`); }
    finally { setBusy(false); }
  }

  // Persist the chosen variant as a custom theme, create the volume, plan it.
  async function createAndPlan() {
    if (!canTopic) return;
    setBusy(true); setMsg('Saving style…');
    const slug = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    try {
      let themeId = baseTheme;
      if (chosenVariant) {
        const sv = await fetch(`/api/${brand}/ebook-styles/select-variant`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: chosenVariant.id, label: chosenVariant.label, description: chosenVariant.description, override: chosenVariant.override }),
        }).then((r) => r.json());
        if (sv?.theme?.id) themeId = sv.theme.id;
      }
      setDesignTheme(themeId);
      setMsg('Creating ebook…');
      const created = await fetch(`/api/${brand}/volumes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug, title: title.trim(), chapters, description: description.trim(),
          ...(themeId && themeId !== 'default' ? { designTheme: themeId } : {}),
          ...(contentStyle && contentStyle !== 'default' ? { contentStyle } : {}),
          ...(designTemplate ? { designTemplate } : {}),
        }),
      }).then((r) => r.json());
      if (created?.error) { setMsg(`Error: ${created.error}`); setBusy(false); return; }

      setMsg('Planning chapters… (this runs the model)');
      const plan = await fetch(`/api/${brand}/volumes/${slug}/plan`, { method: 'POST' }).then((r) => r.json());
      if (plan?.ok) {
        setPlanned((plan.chapters ?? []).map((c: PlannedChapter) => ({ slug: c.slug, title: c.title, purpose: c.purpose, wordTarget: c.wordTarget, _key: chapKey() })));
        setStep('review'); setMsg('');
      } else setMsg(`Error: ${plan?.error ?? 'planning failed'}`);
    } catch (e) { setMsg(`Error: ${(e as Error).message}`); }
    finally { setBusy(false); }
  }

  function editChapter(i: number, patch: Partial<PlannedChapter>) {
    setPlanned((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  }
  function moveChapter(i: number, dir: -1 | 1) {
    setPlanned((cs) => {
      const j = i + dir; if (j < 0 || j >= cs.length) return cs;
      const next = [...cs]; [next[i], next[j]] = [next[j], next[i]]; return next;
    });
  }
  function addChapter() { setPlanned((cs) => [...cs, { title: `Chapter ${cs.length + 1}`, purpose: '', wordTarget: 1100, _key: chapKey() }]); }
  function removeChapter(i: number) { setPlanned((cs) => cs.filter((_, j) => j !== i)); }

  // Save edited chapters, then trigger the full pipeline (compose → render).
  async function saveAndGenerate() {
    const slug = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setBusy(true); setMsg('Saving chapters…');
    try {
      const chapRes = await fetch(`/api/${brand}/volumes/${slug}/chapters`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chapters: planned }),
      });
      const chapData = await chapRes.json().catch(() => ({}));
      if (!chapData?.ok) { setMsg(`Error saving chapters: ${String(chapData?.error ?? 'save failed').slice(0, 200)}`); setBusy(false); return; }
      const pipeData = await fetch(`/api/${brand}/volumes/${slug}/pipeline`, { method: 'POST' }).then((r) => r.json()).catch(() => ({}));
      if (pipeData?.started === false) { setMsg(`Pipeline did not start: ${String(pipeData?.error ?? 'unknown').slice(0, 200)}`); setBusy(false); return; }
      onDone(slug, brand);
    } catch (e) { setMsg(`Error: ${(e as Error).message}`); setBusy(false); }
  }

  const next = () => setStep(STEPS[Math.min(stepIdx + 1, STEPS.length - 1)]);

  return (
    <Modal open onClose={onClose} title="New eBook" hideHeader maxWidth={step === 'preview' || step === 'review' ? 680 : 520}>
      <div style={{ padding: 20 }}>
        <h3 className="modal__title">New eBook</h3>

        <div style={{ display: 'flex', gap: 6, margin: '4px 0 12px', flexWrap: 'wrap' }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: i <= stepIdx ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
              <span style={{ width: 17, height: 17, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, background: i <= stepIdx ? 'var(--accent, #0891b2)' : 'var(--border)', color: i <= stepIdx ? '#fff' : 'var(--text-tertiary)' }}>{i + 1}</span>
              {STEP_LABEL[s]}
            </div>
          ))}
        </div>

        {step === 'brand' && (
          <div>
            <p className="modal__desc">Which brand is this ebook for?</p>
            <PresetCards
              items={EBOOK_BRANDS.map((b) => ({ id: b, label: b, description: b === 'zopdev' ? 'ZopDev' : b === 'zopnight' ? 'ZopNight' : 'ZopDay' }))}
              selected={brand}
              onSelect={setBrand}
            />
          </div>
        )}

        {step === 'topic' && (
          <div>
            <p className="modal__desc">A title and an optional description. The engine researches, plans, and composes each chapter.</p>
            <input className="modal__input" type="text" placeholder="e.g. The Complete Guide to Platform Engineering" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            <textarea className="modal__input" placeholder="Optional: what this ebook covers and who it's for." value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ marginTop: 10, resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Number of chapters</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                  <button key={n} className={`btn ${chapters === n ? 'btn-primary' : 'btn-secondary'}`} style={{ minWidth: 38, padding: '6px 10px', fontSize: 13 }} onClick={() => setChapters(n)}>{n}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'style' && (
          <div>
            {designs.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <p className="modal__desc">Pick a design template — the full ebook layout. Leave unset for the engine default.</p>
                <PresetCards items={designs} selected={designTemplate} onSelect={setDesignTemplate} />
              </div>
            )}
            <p className="modal__desc">Pick a base design style. Upload a designer HTML to add your own.</p>
            <PresetCards items={themes} selected={baseTheme} onSelect={setBaseTheme} />
            <label className="btn btn-secondary" style={{ marginTop: 10, display: 'inline-block', cursor: 'pointer', fontSize: 13 }}>
              ↑ Upload style (.html)
              <input type="file" accept=".html,.htm" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadStyle(f); e.currentTarget.value = ''; }} />
            </label>
          </div>
        )}

        {step === 'voice' && (
          <div>
            <p className="modal__desc">Pick a content style — how the chapters are written. Defaults to the brand voice.</p>
            <PresetCards items={styles} selected={contentStyle} onSelect={setContentStyle} />
          </div>
        )}

        {step === 'preview' && (
          <div>
            <p className="modal__desc">Four variants of your style, tuned to the voice. Click one to see it across four sample pages, then pick it to build the ebook.</p>
            {variants.length === 0 ? (
              <button className="btn btn-secondary" onClick={loadVariants} disabled={busy}>{busy ? 'Working…' : 'Generate 4 variants'}</button>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                  {variants.map((v) => <VariantPreview key={v.id} v={v} selected={chosenVariant?.id === v.id} onSelect={() => setChosenVariant(v)} />)}
                </div>
                {chosenVariant && <VariantPages v={chosenVariant} />}
              </div>
            )}
          </div>
        )}

        {step === 'review' && (
          <div>
            <p className="modal__desc">Review the planned chapters. Edit titles, purposes and word targets; reorder, add or remove. Then generate.</p>
            <div style={{ display: 'grid', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
              {planned.map((c, i) => (
                <div key={c._key ?? i} style={{ border: '1px solid var(--border)', borderRadius: 0, padding: 10 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{i + 1}</span>
                    <input className="modal__input" style={{ margin: 0, flex: 1 }} value={c.title} onChange={(e) => editChapter(i, { title: e.target.value })} placeholder="Chapter title" />
                    <input className="modal__input" type="number" style={{ margin: 0, width: 80 }} value={c.wordTarget} onChange={(e) => editChapter(i, { wordTarget: Number(e.target.value) })} />
                    <button className="btn btn-secondary" style={{ padding: '2px 6px' }} disabled={i === 0} onClick={() => moveChapter(i, -1)}>↑</button>
                    <button className="btn btn-secondary" style={{ padding: '2px 6px' }} disabled={i === planned.length - 1} onClick={() => moveChapter(i, 1)}>↓</button>
                    <button className="btn btn-secondary" style={{ padding: '2px 6px' }} onClick={() => removeChapter(i)}>✕</button>
                  </div>
                  <textarea className="modal__input" style={{ marginTop: 6, resize: 'vertical', fontFamily: 'inherit', fontSize: 12 }} rows={2} value={c.purpose} onChange={(e) => editChapter(i, { purpose: e.target.value })} placeholder="What this chapter covers + which visuals" />
                </div>
              ))}
            </div>
            <button className="btn btn-secondary" style={{ marginTop: 8, fontSize: 13 }} onClick={addChapter}>+ Add chapter</button>
          </div>
        )}

        <div className="modal__actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {stepIdx > 0 && step !== 'review' && <button className="btn btn-secondary" onClick={() => setStep(STEPS[stepIdx - 1])}>← Back</button>}
          {msg && <span style={{ fontSize: 12, color: msg.startsWith('Error') ? 'var(--danger)' : 'var(--text-secondary)', alignSelf: 'center' }}>{msg}</span>}

          {step === 'voice' && (
            <button className="btn btn-primary" onClick={() => { setStep('preview'); loadVariants(); }} disabled={busy}>Preview →</button>
          )}
          {step === 'preview' && (
            <button className="btn btn-primary" onClick={createAndPlan} disabled={busy || !canTopic}>{busy ? 'Working…' : 'Create & plan →'}</button>
          )}
          {step === 'review' && (
            <button className="btn btn-primary" onClick={saveAndGenerate} disabled={busy || planned.length === 0}>{busy ? 'Working…' : 'Save & generate'}</button>
          )}
          {step !== 'voice' && step !== 'preview' && step !== 'review' && (
            <button className="btn btn-primary" onClick={next} disabled={(step === 'topic' && !canTopic) || busy}>Next →</button>
          )}
        </div>
      </div>
    </Modal>
  );
}
